import React, { useEffect, useState } from "react";
import { ShipmentDetail } from "../types";
import { shippingService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { toDateTimeInput, todayLocal } from "../utils/dates";
import "./SendDocumentModal.css";
import "../pages/Receivables.css";
import "../pages/Purchasing.css";
import "../pages/Shipping.css";

export type ShipmentAction = "ship" | "deliver" | "cancel";

interface Props {
  shipment: ShipmentDetail;
  action: ShipmentAction;
  onClose: () => void;
  onDone: (message: string) => void;
}

const nowLocal = () => { const d = new Date(); d.setSeconds(0, 0); return toDateTimeInput(d); };
const today = () => todayLocal();

/**
 * Sevkiyat işlemleri: Sevk Et (irsaliye no + tarih), Teslim Edildi (teslim alan + tarih),
 * İptal (sebep). Tek modal, işlem türüne göre alanlar değişir.
 */
const ShipmentActionModal: React.FC<Props> = ({ shipment, action, onClose, onDone }) => {
  const { hasModule } = usePermissions();
  const [when, setWhen] = useState(nowLocal());
  const [waybillNo, setWaybillNo] = useState(shipment.waybillNo || "");
  const [waybillDate, setWaybillDate] = useState(shipment.waybillDate ? shipment.waybillDate.slice(0, 10) : today());
  const [receivedBy, setReceivedBy] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const stockLines = shipment.lines.length;
  const title = action === "ship" ? "Sevk Et" : action === "deliver" ? "Teslim Edildi" : "Sevkiyatı İptal Et";

  const run = async () => {
    setError("");
    if (action === "cancel" && reason.trim().length < 3) return setError("İptal sebebi en az 3 karakter olmalı.");
    if (action === "deliver" && !receivedBy.trim()) return setError("Teslim alan kişiyi yazın.");
    setSaving(true);
    try {
      if (action === "ship") {
        const r = await shippingService.ship(shipment.shipmentId, {
          shippedAt: new Date(when).toISOString(),
          waybillNo: waybillNo.trim() || null,
          waybillDate: waybillNo.trim() ? waybillDate : null,
        });
        onDone(r.stockMovements > 0 ? `Sevk edildi; ${r.stockMovements} stok çıkışı işlendi.` : "Sevk edildi.");
      } else if (action === "deliver") {
        await shippingService.deliver(shipment.shipmentId, { deliveredAt: new Date(when).toISOString(), receivedBy: receivedBy.trim() });
        onDone("Teslimat onaylandı.");
      } else {
        await shippingService.cancel(shipment.shipmentId, reason.trim());
        onDone("Sevkiyat iptal edildi.");
      }
      onClose();
    } catch (err: any) {
      const d = err?.response?.data;
      setError(typeof d === "string" ? d : d?.message || "İşlem yapılamadı.");
    } finally { setSaving(false); }
  };

  return (
    <div className="ic-overlay rc-page" onClick={() => !saving && onClose()}>
      <div className="ic-panel" style={{ maxWidth: "36rem" }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="ic-bar">
          <div className="ic-bar-left">
            <h2>{title}</h2>
            <span className="ic-chip neutral">{shipment.number}</span>
            <span className="ic-chip neutral">{shipment.firmName}</span>
          </div>
          <button className="ic-close" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>

        <div className="ic-body">
          {error && <div className="ic-alert error">{error}</div>}
          <div className="ic-doc" style={{ padding: "1.25rem 1.5rem" }}>
            {action === "ship" && (
              <div className="rc-form">
                <div className="rc-field full"><label htmlFor="sa-when">Sevk Zamanı</label><input id="sa-when" type="datetime-local" className="ic-in" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
                <div className="rc-field"><label htmlFor="sa-wb">Resmi İrsaliye No</label><input id="sa-wb" className="ic-in" value={waybillNo} maxLength={32} placeholder="Kağıt / portal irsaliye no" onChange={(e) => setWaybillNo(e.target.value)} /></div>
                <div className="rc-field"><label htmlFor="sa-wbd">İrsaliye Tarihi</label><input id="sa-wbd" type="date" className="ic-in" value={waybillDate} disabled={!waybillNo.trim()} onChange={(e) => setWaybillDate(e.target.value)} /></div>
                <div className="rc-field full">
                  <div className="ic-total-hint">
                    {hasModule("stock")
                      ? `${stockLines} satır sevk edilecek; üretim stoğu tanımlı modeller için stoktan ÇIKIŞ işlenir.`
                      : `${stockLines} satır sevk edilecek. Stok modülü lisansta olmadığı için stok bakiyesi değişmez.`}
                    {" "}İrsaliye numarası girilirse bu siparişlerin faturasında irsaliye referansı olarak kullanılır.
                  </div>
                </div>
              </div>
            )}
            {action === "deliver" && (
              <div className="rc-form">
                <div className="rc-field full"><label htmlFor="sa-rb">Teslim Alan</label><input id="sa-rb" className="ic-in" value={receivedBy} maxLength={100} placeholder="Ad soyad / kaşe" autoFocus onChange={(e) => setReceivedBy(e.target.value)} /></div>
                <div className="rc-field full"><label htmlFor="sa-dwhen">Teslim Zamanı</label><input id="sa-dwhen" type="datetime-local" className="ic-in" value={when} onChange={(e) => setWhen(e.target.value)} /></div>
              </div>
            )}
            {action === "cancel" && (
              <div className="rc-form">
                <div className="rc-field full"><label htmlFor="sa-reason">İptal Sebebi</label><input id="sa-reason" className="ic-in" value={reason} maxLength={300} autoFocus placeholder="En az 3 karakter" onChange={(e) => setReason(e.target.value)} /></div>
                <div className="rc-field full">
                  <div className="ic-total-hint">
                    {shipment.lines.some((l) => l.hasStockMovement)
                      ? "Sevk ile üretilen stok çıkışları geri alınır; kayıt iptal olarak kalır."
                      : "Kayıt iptal olarak kalır; sipariş miktarı yeniden sevk edilebilir."}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="ic-actions">
          <span className="ic-actions-summary">{shipment.lines.length} satır · {shipment.totalQuantity.toLocaleString("tr-TR", { maximumFractionDigits: 3 })} birim</span>
          <div className="ic-actions-btns">
            <button className="ic-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button className={`ic-btn ${action === "cancel" ? "danger" : "primary"}`} onClick={run} disabled={saving}>
              {saving ? (<><span className="ic-spin" aria-hidden="true" />İşleniyor…</>) : title}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ShipmentActionModal;
